import React, { useEffect, useMemo, useState } from 'react';
import { clientListAsync, getClientReportSettingsAsync, previewClientReportAsync, updateClientReportSettingsAsync } from '../../Services/AdminService';
import Header from '../../Shared/Components/Header/Header';
import Card from '../../Shared/Components/Card/Card';
import PrimaryButton from '../../Shared/Components/PrimaryButton/PrimaryButton';
import { showToast } from '../../Infrastructure/Notifications/ToastrConfig';

const frequencyLabels = {
    Weekly: 'Weekly report',
    Monthly: 'Monthly report',
    Quarterly: 'Quarterly report'
};

const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const defaultReports = [
    { id: null, frequency: 'Weekly', isEnabled: false, dayOfWeek: null, dayOfMonth: null, reportTime: '', sendToPlatformAdmins: false, sendToClientAdmins: false },
    { id: null, frequency: 'Monthly', isEnabled: false, dayOfWeek: null, dayOfMonth: null, reportTime: '', sendToPlatformAdmins: false, sendToClientAdmins: false },
    { id: null, frequency: 'Quarterly', isEnabled: false, dayOfWeek: null, dayOfMonth: null, reportTime: '', sendToPlatformAdmins: false, sendToClientAdmins: false },
];

const cloneReports = (reports) => defaultReports.map(defaultReport => {
    const match = reports?.find(report => report.frequency === defaultReport.frequency);
    return match ? { ...defaultReport, ...match } : { ...defaultReport };
});

const AutomationSettings = () => {
    const [clients, setClients] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [reports, setReports] = useState(defaultReports);
    const [previewPeriod, setPreviewPeriod] = useState('Weekly');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');
    const [previewError, setPreviewError] = useState('');

    useEffect(() => {
        const loadClients = async () => {
            try {
                const response = await clientListAsync();
                const availableClients = Array.isArray(response) ? response : response?.clients || [];
                setClients(availableClients);
                if (availableClients.length > 0) {
                    setSelectedClientId(current => current || availableClients[0].id);
                }
            } catch (error) {
                console.error('Failed to load clients for automation settings', error);
                showToast(false, 'Unable to load clients');
            }
        };

        loadClients();
    }, []);

    useEffect(() => {
        if (!selectedClientId) {
            return;
        }

        const loadSettings = async () => {
            setLoading(true);
            try {
                const response = await getClientReportSettingsAsync(selectedClientId);
                const settings = response?.reports || response || [];
                setReports(settings.length > 0 ? cloneReports(settings) : defaultReports);
            } catch (error) {
                console.error('Failed to load automation settings', error);
                showToast(false, 'Unable to load automation settings for that client');
            } finally {
                setLoading(false);
            }
        };

        loadSettings();
    }, [clients, selectedClientId]);

    const selectedClient = useMemo(() => clients.find(client => client.id === selectedClientId), [clients, selectedClientId]);

    const handleClientChange = evt => {
        const clientId = evt.target.value;
        setSelectedClientId(clientId);
        setPreviewHtml('');
        setPreviewError('');
    };

    const handlePreview = async () => {
        if (!selectedClientId) {
            showToast(false, 'Select a client first');
            return;
        }

        setPreviewLoading(true);
        setPreviewError('');
        setPreviewHtml('');

        try {
            const response = await previewClientReportAsync(selectedClientId, previewPeriod);
            const html = typeof response === 'string'
                ? response
                : response?.html || response?.previewHtml || response?.content || '';

            if (!html) {
                throw new Error('No preview HTML returned');
            }

            setPreviewHtml(html);
        } catch (error) {
            console.error('Failed to preview client report', error);
            setPreviewError('Unable to load preview for the selected client and period.');
            showToast(false, 'Unable to load report preview');
        } finally {
            setPreviewLoading(false);
        }
    };

    const updateReport = (frequency, field, value) => {
        setReports(currentReports => currentReports.map(report => report.frequency === frequency ? { ...report, [field]: value } : report));
    };

    const validateReports = () => {
        for (const report of reports) {
            if (!report.isEnabled) {
                continue;
            }

            if (!report.sendToPlatformAdmins && !report.sendToClientAdmins) {
                showToast(false, `Choose at least one recipient for the ${report.frequency.toLowerCase()} report`);
                return false;
            }

            if (!/^\d{2}:\d{2}$/.test(report.reportTime)) {
                showToast(false, `${report.frequency} report time must be in HH:mm format`);
                return false;
            }

            const [hours, minutes] = report.reportTime.split(':').map(Number);
            if (hours > 23 || minutes > 59) {
                showToast(false, `${report.frequency} report time must be a valid clock time`);
                return false;
            }

            if (report.frequency === 'Weekly') {
                if (report.dayOfWeek === null || report.dayOfWeek === undefined || report.dayOfWeek < 0 || report.dayOfWeek > 6) {
                    showToast(false, 'Weekly reports must use a day of week between Sunday and Saturday');
                    return false;
                }
            } else if (report.frequency === 'Monthly' || report.frequency === 'Quarterly') {
                if (report.dayOfMonth === null || report.dayOfMonth === undefined || report.dayOfMonth < 1 || report.dayOfMonth > 28) {
                    showToast(false, `${report.frequency} reports must use a day of month between 1 and 28`);
                    return false;
                }
            }
        }

        return true;
    };

    const handleSave = async () => {
        if (!selectedClientId) {
            showToast(false, 'Select a client first');
            return;
        }

        if (!validateReports()) {
            return;
        }

        setSaving(true);
        try {
            const response = await updateClientReportSettingsAsync({
                clientId: selectedClientId,
                reports
            });

            if (response?.success === false) {
                showToast(false, response?.message || 'Unable to save automation settings');
                return;
            }

            showToast(true, 'Automation settings saved');
        } catch (error) {
            console.error('Failed to save automation settings', error);
            showToast(false, 'Unable to save automation settings');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Header heading="Reports Automation" subheading="Configure scheduled reports for each client" icon="stats-up" />
            <div className="container-fluid content">
                <Card title="Client Selection">
                    <div className="row align-items-end">
                        <div className="col-12 col-lg-6 mb-3 mb-lg-0">
                            <label htmlFor="clientId">Client</label>
                            <select id="clientId" className="form-control" value={selectedClientId} onChange={handleClientChange}>
                                <option value="">Select a client</option>
                                {clients.map(client => (
                                    <option key={client.id} value={client.id}>{client.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-12 col-lg-6">
                            <div className="alert alert-light mb-0">
                                {selectedClient ? `Managing automation for ${selectedClient.name}` : 'Choose a client to load its automation settings.'}
                            </div>
                        </div>
                    </div>
                </Card>

                <Card title="Report Preview">
                    <div className="row align-items-end">
                        <div className="col-12 col-md-4 mb-3 mb-md-0">
                            <label htmlFor="previewPeriod">Preview period</label>
                            <select
                                id="previewPeriod"
                                className="form-control"
                                value={previewPeriod}
                                onChange={evt => setPreviewPeriod(evt.target.value)}
                            >
                                <option value="Weekly">Weekly</option>
                                <option value="Monthly">Monthly</option>
                                <option value="Quarterly">Quarterly</option>
                            </select>
                        </div>
                        <div className="col-12 col-md-8 d-flex justify-content-md-end">
                            <PrimaryButton
                                text={previewLoading ? 'Loading preview...' : 'Load Preview'}
                                func={handlePreview}
                                disabled={previewLoading || !selectedClientId}
                                icon="search"
                            />
                        </div>
                    </div>

                    <div className="mt-3">
                        {!previewHtml && !previewError && (
                            <div className="alert alert-light border mb-0">
                                Choose a client and period, then load the preview to see the rendered HTML.
                            </div>
                        )}

                        {previewError && (
                            <div className="alert alert-warning mb-0">
                                {previewError}
                            </div>
                        )}

                        {previewHtml && (
                            <iframe
                                title="Report preview"
                                srcDoc={previewHtml}
                                style={{ width: '100%', minHeight: '700px', border: '1px solid #e5e7eb', borderRadius: '12px', background: '#fff' }}
                                sandbox=""
                            />
                        )}
                    </div>
                </Card>

                <Card title="Scheduled Reports">
                    {loading ? (
                        <div className="alert alert-info mb-0">Loading automation settings...</div>
                    ) : (
                        <div className="row">
                            {reports.map(report => (
                                <div key={report.frequency} className="col-12 col-xl-4 mb-3">
                                    <div className="border rounded p-3 h-100">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div>
                                                <h5 className="mb-1">{frequencyLabels[report.frequency]}</h5>
                                                <small className="text-muted">Configure when and who receives this report.</small>
                                            </div>
                                            <div className="custom-control custom-switch">
                                                <input
                                                    type="checkbox"
                                                    className="custom-control-input"
                                                    id={`${report.frequency}-enabled`}
                                                    checked={report.isEnabled}
                                                    onChange={evt => updateReport(report.frequency, 'isEnabled', evt.target.checked)}
                                                />
                                                <label className="custom-control-label" htmlFor={`${report.frequency}-enabled`}>
                                                    {report.isEnabled ? 'On' : 'Off'}
                                                </label>
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label>Send time</label>
                                            <input
                                                type="time"
                                                className="form-control"
                                                value={report.reportTime}
                                                onChange={evt => updateReport(report.frequency, 'reportTime', evt.target.value)}
                                            />
                                        </div>

                                        {report.frequency === 'Weekly' && (
                                            <div className="form-group">
                                                <label>Day of week</label>
                                                <select
                                                    className="form-control"
                                                    value={report.dayOfWeek ?? ''}
                                                    onChange={evt => updateReport(report.frequency, 'dayOfWeek', Number(evt.target.value))}
                                                >
                                                    <option value="">Select a day</option>
                                                    {dayNames.map((day, index) => <option key={day} value={index}>{day}</option>)}
                                                </select>
                                            </div>
                                        )}

                                        {(report.frequency === 'Monthly' || report.frequency === 'Quarterly') && (
                                            <div className="form-group">
                                                <label>Day of month</label>
                                                <select
                                                    className="form-control"
                                                    value={report.dayOfMonth ?? ''}
                                                    onChange={evt => updateReport(report.frequency, 'dayOfMonth', Number(evt.target.value))}
                                                >
                                                    <option value="">Select a day</option>
                                                    {Array.from({ length: 28 }, (_, index) => index + 1).map(day => (
                                                        <option key={day} value={day}>{day}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        <div className="form-group mb-2">
                                            <label>Recipients</label>
                                            <div className="custom-control custom-checkbox">
                                                <input
                                                    type="checkbox"
                                                    className="custom-control-input"
                                                    id={`${report.frequency}-platform-admins`}
                                                    checked={report.sendToPlatformAdmins}
                                                    onChange={evt => updateReport(report.frequency, 'sendToPlatformAdmins', evt.target.checked)}
                                                />
                                                <label className="custom-control-label" htmlFor={`${report.frequency}-platform-admins`}>
                                                    Platform admins
                                                </label>
                                            </div>
                                            <div className="custom-control custom-checkbox mt-2">
                                                <input
                                                    type="checkbox"
                                                    className="custom-control-input"
                                                    id={`${report.frequency}-client-admins`}
                                                    checked={report.sendToClientAdmins}
                                                    onChange={evt => updateReport(report.frequency, 'sendToClientAdmins', evt.target.checked)}
                                                />
                                                <label className="custom-control-label" htmlFor={`${report.frequency}-client-admins`}>
                                                    Client admins
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="d-flex justify-content-end mt-3">
                        <PrimaryButton text={saving ? 'Saving...' : 'Save Automation Settings'} func={handleSave} disabled={saving || !selectedClientId} icon="checkmark" />
                    </div>
                </Card>
            </div>
        </>
    );
};

export default AutomationSettings;